import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Bot, Undo2 } from 'lucide-react'
import { trackEvent } from '../../../../lib/gtag'
import { useNotesQuery, useReopenPass } from '../../hooks/use-investor-assistant'
import { formatShortDate } from './dates'
import { roundLabel } from './round'
import { StartupTile } from './startup-tile'

const formatDate = formatShortDate

const REASON: Record<string, string> = {
  NOT_A_FIT: 'Not a fit',
  TOO_EARLY: 'Too early',
  NOT_DEPLOYING: 'Not investing now',
  UNSPECIFIED: 'No reason given',
}

export function PassedTab({
  openId,
  onOpen,
}: {
  openId: string | null
  onOpen: (startupId: string) => void
}) {
  const notes = useNotesQuery()
  const reopen = useReopenPass()
  const passes = notes.data?.data.passes ?? []

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Passed startups stay out of your watchdog.</p>
      {passes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No passes.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="py-3 pl-7 pr-4 font-medium">Startup</th>
                <th className="px-4 py-3 font-medium">Round</th>
                <th className="px-4 py-3 font-medium">Passed</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3 font-medium">Would look again</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {passes.map((pass) => (
                <tr
                  key={pass.startupId}
                  data-startup-id={pass.startupId}
                  onClick={(e) => {
                    // Only a click on the row itself opens the panel; the Undo button does its own thing.
                    if ((e.target as HTMLElement).closest('button, a')) return
                    onOpen(pass.startupId)
                  }}
                  className={`border-t border-border text-sm hover:bg-white/[0.04] ${openId === pass.startupId ? 'bg-white/5' : ''}`}
                >
                  <td className="py-3 pl-7 pr-4">
                    <div className="flex items-center gap-3">
                      <StartupTile name={pass.startupName} />
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => onOpen(pass.startupId)}
                          className="whitespace-nowrap text-left font-medium text-foreground hover:underline"
                        >
                          {pass.startupName}
                        </button>
                        {/* Only the AI can pass today; an in-app pass will say who made it. */}
                        <p className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                          <Bot className="size-3.5" />
                          through your AI
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-foreground">
                    {roundLabel(pass.round)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-foreground">
                    {formatDate(pass.date)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-foreground">
                    {REASON[pass.outcome] ?? pass.outcome}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {pass.revisitWhen ?? 'Not set'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <SecondaryButton
                      size="sm"
                      icon={<Undo2 className="size-3.5" />}
                      disabled={reopen.isPending}
                      onClick={() => {
                        trackEvent('assistant_pass_reopened', { startup_id: pass.startupId })
                        reopen.mutate({ path: { startupId: pass.startupId } })
                      }}
                    >
                      Undo pass
                    </SecondaryButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
